import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import logo from '../assets/img/trustlab-logo.png';
import { useGetContactInfoQuery } from '../store/siteContentApi';

// The holding page the whole public site is replaced by while
// REACT_APP_COMING_SOON is on. See routes/holding.js for exactly which
// routes it covers and which stay open.
//
// Deliberately self-contained: it declares its own colours and type and
// imports no shared style module, so it keeps working whatever else
// changes around it. The only things it depends on are the logo and the
// contact details.
//
// It reads phone / WhatsApp / email from the admin's ContactInfo so the
// numbers stay correct, and falls back to the published ones if that
// request fails — a holding page nobody can ring is worse than none.
const C = {
  forest: '#152A1E',
  forestM: '#1E3D2A',
  sage: '#3D7A52',
  sageL: '#6AAE80',
  sageP: '#C8EDD8',
  cream: '#FAF6EF',
};

const FONT = "'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const BADGES = ['NABL Accredited', 'ISO 15189', 'ICMR Approved'];

const Glyph = ({ paths }) => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    sx={{ width: 22, height: 22, flexShrink: 0 }}
  >
    {paths.map((d) => <path key={d} d={d} />)}
  </Box>
);

const ComingSoon = () => {
  const { data: contact } = useGetContactInfoQuery();

  const phone = contact?.phone || '74400 75400';
  const whatsapp = contact?.whatsapp || '+919014638633';
  const email = contact?.email || 'support@mytrustlab.com';

  const channels = [
    {
      href: `tel:${String(phone).replace(/\s/g, '')}`,
      t: 'Call us',
      sub: contact?.support_hours || 'Mon – Sat, 6 AM – 10 PM',
      paths: ['M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z'],
    },
    {
      href: `https://wa.me/${String(whatsapp).replace(/\D/g, '')}`,
      external: true,
      t: 'WhatsApp',
      sub: 'Response in under 5 minutes',
      paths: ['M3.5 20.5l1.4-4.2A8.5 8.5 0 1 1 8 19.3z'],
    },
    {
      href: `mailto:${email}`,
      t: 'Email us',
      sub: email,
      paths: ['M3 7l9 6 9-6', 'M3 5h18v14H3z'],
    },
  ];

  return (
    <Box
      component="main"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        bgcolor: C.forest,
        color: '#fff',
        fontFamily: FONT,
        py: { xs: 6, md: 10 },
        px: { xs: 2.5, md: 5 },
      }}
    >
      {/* Soft wash off the top-right corner, clipped by the section. */}
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          right: '-14%',
          top: '-32%',
          width: '62vw',
          maxWidth: 900,
          aspectRatio: '1',
          borderRadius: '50%',
          background: `radial-gradient(circle at 50% 50%, ${C.sage}3D, transparent 62%)`,
          pointerEvents: 'none',
        }}
      />

      <Box sx={{ position: 'relative', width: '100%', maxWidth: 980, mx: 'auto' }}>
        <Box
          component="img"
          src={logo}
          alt="TrustLab Diagnostics"
          sx={{ width: { xs: 150, md: 190 }, height: 'auto', display: 'block', mb: 3 }}
        />

        <Box
          component="span"
          sx={{
            display: 'inline-block',
            py: 0.75,
            px: 1.75,
            mb: 2.5,
            borderRadius: 999,
            bgcolor: 'rgba(255,255,255,0.08)',
            color: C.sageP,
            fontFamily: FONT,
            fontSize: 12.5,
            fontWeight: 700,
            letterSpacing: '0.04em',
          }}
        >
          Back shortly
        </Box>

        <Typography
          component="h1"
          sx={{
            fontFamily: FONT,
            fontSize: { xs: 40, sm: 56, md: 76 },
            fontWeight: 700,
            lineHeight: 1.03,
            letterSpacing: '-0.03em',
            maxWidth: '16ch',
            m: 0,
            // Explicit, because MUI's h1 variant carries the old theme's
            // dark body colour — inheriting left the headline grey on
            // the dark panel while only the accent half looked right.
            color: '#fff',
          }}
        >
          Something better is{' '}
          <Box component="span" sx={{ color: C.sageL }}>coming soon.</Box>
        </Typography>

        <Typography
          sx={{
            fontFamily: FONT,
            fontSize: { xs: 16, md: 18 },
            lineHeight: 1.65,
            color: 'rgba(255,255,255,0.72)',
            maxWidth: 620,
            mt: 2.5,
          }}
        >
          We&rsquo;re rebuilding mytrustlab.com. Online booking is paused while we
          do — but the lab is open and our team is on the phone, so your tests,
          collections and reports carry on exactly as normal.
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 3 }}>
          {BADGES.map((b) => (
            <Box
              key={b}
              component="span"
              sx={{
                py: 0.75,
                px: 1.75,
                borderRadius: 999,
                bgcolor: 'rgba(255,255,255,0.08)',
                color: '#fff',
                fontFamily: FONT,
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              {b}
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))',
            gap: 1.5,
            mt: { xs: 4, md: 5 },
          }}
        >
          {channels.map((c) => (
            <Box
              key={c.t}
              component="a"
              href={c.href}
              {...(c.external ? { target: '_blank', rel: 'noopener noreferrer' } : null)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.75,
                p: 2.25,
                borderRadius: '16px',
                bgcolor: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.12)',
                color: '#fff',
                textDecoration: 'none',
                transition: 'background-color .25s, transform .25s',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', transform: 'translateY(-3px)' },
              }}
            >
              <Box
                component="span"
                sx={{
                  display: 'grid',
                  placeItems: 'center',
                  width: 44,
                  height: 44,
                  flexShrink: 0,
                  borderRadius: '13px',
                  bgcolor: 'rgba(106,174,128,0.18)',
                  color: C.sageP,
                }}
              >
                <Glyph paths={c.paths} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ fontFamily: FONT, fontSize: 15.5, fontWeight: 700 }}>{c.t}</Box>
                <Box
                  sx={{
                    fontFamily: FONT,
                    fontSize: 13.5,
                    color: 'rgba(255,255,255,0.6)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {c.sub}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>

        <Typography
          sx={{
            fontFamily: FONT,
            fontSize: 13,
            color: 'rgba(255,255,255,0.4)',
            mt: 4,
          }}
        >
          © {new Date().getFullYear()} TrustLab Diagnostics Pvt. Ltd. · Hyderabad
        </Typography>
      </Box>
    </Box>
  );
};

export default ComingSoon;
